/**
 * Shared profile form used by both ProfileOnboardingModal (step 2) and ProfileEditModal.
 * Handles avatar picker, bio, TikTok, Instagram.
 */
import { useState } from 'react';
import { Instagram, Check, Loader } from 'lucide-react';
import AvatarDisplay from './AvatarDisplay';
import { AVATAR_COUNT } from '../config/avatarConfig';
import { savePublicProfile, clearProfileFields } from '../services/profileService';

// ─── TikTok icon ──────────────────────────────────────────────────────────────

export function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
    </svg>
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────

const HANDLE_RE = /^[a-zA-Z0-9._-]{1,30}$/;

function validateHandle(value) {
  if (!value) return null;
  if (!HANDLE_RE.test(value)) return 'Only letters, numbers, dots, dashes and underscores (max 30)';
  return null;
}

function sanitizeDescription(value) {
  return value
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
    .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '');
}

// ─── ProfileForm ──────────────────────────────────────────────────────────────

/**
 * @param {string}   username       - profile owner's username
 * @param {object}   initialData    - { description, tiktok, instagram, avatar } — pre-filled for edit
 * @param {function} onDone         - called with saved profile data on success
 * @param {function} onBack         - optional back button handler (onboarding only)
 * @param {string}   saveLabel      - button label, e.g. "Create profile" or "Save changes"
 * @param {boolean}  isEdit         - if true, handles field deletion for cleared inputs
 */
export default function ProfileForm({ username, initialData = {}, onDone, onBack, saveLabel = 'Save', isEdit = false }) {
  const [description, setDescription] = useState(initialData.description || '');
  const [tiktok, setTiktok] = useState(initialData.tiktok || '');
  const [instagram, setInstagram] = useState(initialData.instagram || '');
  const [avatar, setAvatar] = useState(typeof initialData.avatar === 'number' ? initialData.avatar : 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const tiktokError = validateHandle(tiktok);
  const instagramError = validateHandle(instagram);
  const canSave = !tiktokError && !instagramError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');

    const data = { avatar };
    if (description.trim()) data.description = description.trim();
    if (tiktok.trim()) data.tiktok = tiktok.trim();
    if (instagram.trim()) data.instagram = instagram.trim();

    // For edits: delete fields that were cleared
    if (isEdit) {
      const toDelete = [];
      if (!description.trim() && initialData.description) toDelete.push('description');
      if (!tiktok.trim() && initialData.tiktok) toDelete.push('tiktok');
      if (!instagram.trim() && initialData.instagram) toDelete.push('instagram');
      if (toDelete.length) await clearProfileFields(username, toDelete);
    }

    const result = await savePublicProfile(username, data);
    if (!result.success) {
      setError(result.error || 'Something went wrong. Please try again.');
      setSaving(false);
      return;
    }

    setSaving(false);
    onDone({ description: data.description, tiktok: data.tiktok, instagram: data.instagram, avatar });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Form fields */}
      <div className="flex-1 overflow-y-auto space-y-5 px-1">

        {/* Avatar picker */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Avatar</label>
          <div className="grid grid-cols-7 gap-2">
            <button
              onClick={() => setAvatar(0)}
              className={`rounded-full transition-all flex items-center justify-center w-fit mx-auto ${avatar === 0 ? 'ring-2 ring-amber-400 ring-offset-2' : 'hover:ring-2 hover:ring-slate-200 hover:ring-offset-2'}`}
            >
              <AvatarDisplay avatar={0} username={username} size="sm" />
            </button>
            {Array.from({ length: AVATAR_COUNT }, (_, i) => i + 1).map(i => (
              <button
                key={i}
                onClick={() => setAvatar(i)}
                className={`rounded-full transition-all flex items-center justify-center w-fit mx-auto ${avatar === i ? 'ring-2 ring-amber-400 ring-offset-2' : 'hover:ring-2 hover:ring-slate-200 hover:ring-offset-2'}`}
              >
                <AvatarDisplay avatar={i} username={username} size="sm" />
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Avatars by <a href="https://dicebear.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">DiceBear</a>, adventurer-neutral style, CC BY 4.0
          </p>
        </div>

        {/* Bio */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Bio <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <div className="relative">
            <textarea
              rows={2}
              value={description}
              onChange={e => setDescription(sanitizeDescription(e.target.value).slice(0, 50))}
              placeholder="A short line about you or your art..."
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all resize-none pb-5"
            />
            <span className="absolute right-3 bottom-2 text-[11px] text-slate-400">
              {description.length}/50
            </span>
          </div>
        </div>

        {/* TikTok */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            TikTok <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <TikTokIcon size={14} />
            </span>
            <input
              type="text"
              value={tiktok}
              onChange={e => setTiktok(e.target.value)}
              placeholder="your_handle"
              className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
            />
          </div>
          {tiktokError && <p className="text-xs text-red-500 mt-1">{tiktokError}</p>}
        </div>

        {/* Instagram */}
        <div className="pb-1">
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Instagram <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Instagram size={14} />
            </span>
            <input
              type="text"
              value={instagram}
              onChange={e => setInstagram(e.target.value)}
              placeholder="your_handle"
              className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
            />
          </div>
          {instagramError && <p className="text-xs text-red-500 mt-1">{instagramError}</p>}
        </div>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 flex gap-3">
        {onBack && (
          <button
            onClick={onBack}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors disabled:opacity-50"
          >
            ← Back
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
        >
          {saving ? <><Loader size={15} className="animate-spin" /> Saving…</> : <><Check size={15} /> {saveLabel}</>}
        </button>
      </div>
    </div>
  );
}
